/**
 * パンくずリスト設定
 * 各ルートとそのパンくずリスト情報を定義
 */

import { featureFlags } from '../utils/featureFlags';

export interface BreadcrumbItem {
  path: string;
  title: string;
  dynamic?: boolean; // 動的タイトル（:id などを含むルート）
}

export interface BreadcrumbConfig {
  [key: string]: BreadcrumbItem;
}

// メインアプリケーション（/app）のパンくずリスト設定
export const appBreadcrumbs: BreadcrumbConfig = {
  '/app/dashboard': { path: '/app/dashboard', title: 'ダッシュボード' },
  '/app/locations': { path: '/app/locations', title: 'エリア管理' },
  '/app/equipments': { path: '/app/equipments', title: '設備管理' },
  '/app/equipments/:id': { path: '/app/equipments', title: '設備詳細', dynamic: true },
  '/app/equipments/:id/edit': { path: '/app/equipments', title: '設備編集', dynamic: true },
  '/app/deterioration': { path: '/app/deterioration', title: '健全度予測' },
  '/app/deterioration/:id': { path: '/app/deterioration', title: '健全度予測詳細', dynamic: true },
  '/app/maintenance': { path: '/app/maintenance', title: '保全カレンダー' },
  '/app/maintenance/recurring-settings': { path: '/app/maintenance', title: '繰り返し設定' },
  '/app/maintenance/:id': { path: '/app/maintenance', title: 'スケジュール詳細', dynamic: true },
  '/app/schedule/:id': { path: '/app/maintenance', title: 'スケジュール詳細', dynamic: true },
  '/app/my-tasks': { path: '/app/my-tasks', title: 'マイタスク' },
  '/app/work-order-templates': { path: '/app/work-order-templates', title: '作業指示書' },
  '/app/work-order-templates/:id': { path: '/app/work-order-templates', title: '作業指示書詳細', dynamic: true },
  '/app/work-history': { path: '/app/work-history', title: '作業履歴一覧' },
  '/app/work-history/:id': { path: '/app/work-history', title: '作業履歴詳細', dynamic: true },
  '/app/emergency-works': { path: '/app/emergency-works', title: '緊急作業' },
  '/app/emergency-works/:id': { path: '/app/emergency-works', title: '緊急作業詳細', dynamic: true },
  '/app/parts': { path: '/app/parts', title: '部品・消耗品管理' },
  '/app/parts/:id': { path: '/app/parts', title: '部品・消耗品詳細', dynamic: true },
  '/app/suppliers': { path: '/app/suppliers', title: '業者管理' },
  '/app/suppliers/:id': { path: '/app/suppliers', title: '業者詳細', dynamic: true },
  '/app/stock-dashboard': { path: '/app/stock-dashboard', title: '在庫ダッシュボード' },
  '/app/purchase-orders': { path: '/app/purchase-orders', title: '発注管理' },
  '/app/purchase-orders/category-orders': { path: '/app/purchase-orders', title: 'カテゴリ別 発注・消費・追加' },
  '/app/purchase-orders/category-orders/:categoryId': { path: '/app/purchase-orders/category-orders', title: 'カテゴリ別 発注・消費・追加', dynamic: true },
  '/app/purchase-orders/create': { path: '/app/purchase-orders', title: '発注書ドラフト' },
  '/app/purchase-orders/:id/pdf': { path: '/app/purchase-orders', title: '発注書PDF確認', dynamic: true },
  '/app/purchase-orders/:id': { path: '/app/purchase-orders', title: '発注詳細', dynamic: true },
  '/app/parts-analytics': { path: '/app/parts-analytics', title: '部品・消耗品分析ダッシュボード' },
  '/app/part-consumption-logs': { path: '/app/part-consumption-logs', title: '部品・消耗品消費管理' },
  '/app/notifications': { path: '/app/notifications', title: '通知一覧' },
  '/app/memos': { path: '/app/memos', title: '一元メモ設定' },
  '/app/profile': { path: '/app/profile', title: 'プロフィール' },
  '/app/profile/notifications': { path: '/app/profile', title: '通知設定' },
  '/app/announcements': { path: '/app/announcements', title: 'お知らせ' },
  '/app/categories': { path: '/app/categories', title: 'カテゴリ管理' },
  ...(featureFlags.qrLabelPrinting
    ? { '/app/qr-labels': { path: '/app/qr-labels', title: 'QRコードシール印刷' } }
    : {}),
  '/app/import': { path: '/app/import', title: 'インポート' },
  '/app/export': { path: '/app/export', title: 'エクスポート' },
  '/app/import-export': { path: '/app/import-export', title: 'インポート/エクスポート' },
  '/app/subscription/bank-transfer/confirmation': { path: '/app/subscription', title: '銀行振込確認' },
  '/app/subscription/addon': { path: '/app/subscription', title: 'アドオン購入' },
};

// 設定ページ（/app/settings）のパンくずリスト設定
export const settingsBreadcrumbs: BreadcrumbConfig = {
  '/app/settings': { path: '/app/settings', title: '会社設定' },
  '/app/settings/dashboard': { path: '/app/settings/dashboard', title: '会社設定ダッシュボード' },
  '/app/settings/company': { path: '/app/settings/company', title: '会社情報' },
  '/app/settings/branches': { path: '/app/settings/branches', title: 'マップ管理' },
  '/app/settings/system-config': { path: '/app/settings/system-config', title: 'システム設定' },
  '/app/settings/storage': { path: '/app/settings/storage', title: 'ストレージ管理' },
  '/app/settings/users': { path: '/app/settings/users', title: 'ユーザー管理' },
  '/app/settings/subscription': { path: '/app/settings/subscription', title: '契約プラン' },
  '/app/settings/import': { path: '/app/settings/import', title: 'データインポート' },
  '/app/settings/export': { path: '/app/settings/export', title: 'データエクスポート' },
};

// 管理画面（/admin）のパンくずリスト設定
export const adminBreadcrumbs: BreadcrumbConfig = {
  '/admin': { path: '/admin', title: '管理画面' },
  '/admin/dashboard': { path: '/admin/dashboard', title: 'ダッシュボード' },
  '/admin/companies': { path: '/admin/companies', title: '会社一覧' },
  '/admin/companies/:companyId': { path: '/admin/companies', title: '会社詳細', dynamic: true },
  '/admin/subscriptions': { path: '/admin/subscriptions', title: 'サブスク管理' },
  '/admin/subscriptions/bank-transfers': { path: '/admin/subscriptions', title: '銀行振込管理' },
  '/admin/subscriptions/plan-master': { path: '/admin/subscriptions', title: 'プランマスタ' },
  '/admin/subscriptions/coupons': { path: '/admin/subscriptions', title: 'クーポン管理' },
  '/admin/agencies': { path: '/admin/agencies', title: '代理店一覧' },
  '/admin/agencies/:id': { path: '/admin/agencies', title: '代理店詳細', dynamic: true },
  '/admin/agencies/commissions': { path: '/admin/agencies', title: '手数料管理' },
  '/admin/partners': { path: '/admin/partners', title: 'パートナー一覧' },
  '/admin/partners/:id': { path: '/admin/partners', title: 'パートナー詳細', dynamic: true },
  '/admin/announcements': { path: '/admin/announcements', title: 'お知らせ管理' },
  '/admin/users': { path: '/admin/users', title: 'ユーザー一覧' },
  '/admin/admin-users': { path: '/admin/admin-users', title: '管理ユーザー' },
  '/admin/audit-log': { path: '/admin/audit-log', title: '監査ログ' },
  '/admin/dev-tools': { path: '/admin/dev-tools', title: '開発ツール' },
  '/admin/settings/access-templates': { path: '/admin/settings', title: 'アクセス制御テンプレート' },
  '/admin/settings/maintenance': { path: '/admin/settings', title: 'メンテナンスモード' },
};

// 代理店ポータル（/agency）のパンくずリスト設定
export const agencyBreadcrumbs: BreadcrumbConfig = {
  '/agency': { path: '/agency', title: '代理店ポータル' },
  '/agency/dashboard': { path: '/agency/dashboard', title: 'ダッシュボード' },
  '/agency/coupons': { path: '/agency/coupons', title: 'クーポン管理' },
  '/agency/commissions': { path: '/agency/commissions', title: '手数料・売上' },
  '/agency/profile': { path: '/agency/profile', title: 'プロフィール' },
};

// パートナーポータル（/partner）のパンくずリスト設定
export const partnerBreadcrumbs: BreadcrumbConfig = {
  '/partner': { path: '/partner', title: 'パートナーポータル' },
  '/partner/dashboard': { path: '/partner/dashboard', title: 'ダッシュボード' },
  '/partner/companies': { path: '/partner/companies', title: '担当会社' },
  '/partner/companies/new': { path: '/partner/companies', title: '新規会社登録' },
  '/partner/companies/:companyId': { path: '/partner/companies', title: '会社詳細', dynamic: true },
  '/partner/requests': { path: '/partner/requests', title: '申請一覧' },
  '/partner/users': { path: '/partner/users', title: 'ユーザー管理' },
};

// 共有ユーザー（/shared-app）のパンくずリスト設定
export const sharedAppBreadcrumbs: BreadcrumbConfig = {
  '/shared-app': { path: '/shared-app', title: 'ダッシュボード' },
  '/shared-app/dashboard': { path: '/shared-app/dashboard', title: 'ダッシュボード' },
  '/shared-app/maintenance': { path: '/shared-app/maintenance', title: '保全カレンダー' },
  '/shared-app/maintenance/:id': { path: '/shared-app/maintenance', title: 'スケジュール詳細', dynamic: true },
  '/shared-app/work-history': { path: '/shared-app/work-history', title: '作業履歴一覧' },
  '/shared-app/work-history/:id': { path: '/shared-app/work-history', title: '作業履歴詳細', dynamic: true },
  '/shared-app/emergency-works': { path: '/shared-app/emergency-works', title: '緊急作業' },
  '/shared-app/emergency-works/:id': { path: '/shared-app/emergency-works', title: '緊急作業詳細', dynamic: true },
  '/shared-app/my-tasks': { path: '/shared-app/my-tasks', title: 'マイタスク' },
};

// 全ての設定を統合
export const allBreadcrumbs: BreadcrumbConfig = {
  ...appBreadcrumbs,
  ...settingsBreadcrumbs,
  ...adminBreadcrumbs,
  ...agencyBreadcrumbs,
  ...partnerBreadcrumbs,
  ...sharedAppBreadcrumbs,
};

/**
 * パスに基づいてパンくずリスト設定を取得
 * 動的パラメータ（:id など）を含むパスにも対応
 */
export function getBreadcrumbConfig(pathname: string): BreadcrumbItem | null {
  // 完全一致を最初にチェック
  if (allBreadcrumbs[pathname]) {
    return allBreadcrumbs[pathname];
  }

  // 動的パラメータを含むパスをチェック
  for (const [pattern, config] of Object.entries(allBreadcrumbs)) {
    if (config.dynamic) {
      // パターンを正規表現に変換（:param を [^/]+ にマッチ）
      const regexPattern = pattern.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${regexPattern}$`);
      if (regex.test(pathname)) {
        return config;
      }
    }
  }

  return null;
}

/**
 * パスからパンくずリストの階層を生成
 */
export function generateBreadcrumbTrail(
  pathname: string,
  layoutType: 'app' | 'settings' | 'admin' | 'agency' | 'partner' | 'shared-app'
): BreadcrumbItem[] {
  const trail: BreadcrumbItem[] = [];
  const config = getBreadcrumbConfig(pathname);

  if (!config) {
    return trail;
  }

  // レイアウトに応じたルートを追加
  switch (layoutType) {
    case 'app':
      // ダッシュボード以外の場合はホームを追加
      if (pathname !== '/app/dashboard') {
        trail.push({ path: '/app/dashboard', title: 'ホーム' });
      }
      break;
    case 'settings':
      // 設定ダッシュボード以外の場合は設定ホームを追加
      if (pathname !== '/app/settings' && pathname !== '/app/settings/dashboard') {
        trail.push({ path: '/app/settings/dashboard', title: '会社設定' });
      }
      break;
    case 'admin':
      // 管理画面ダッシュボード以外の場合は管理画面ホームを追加
      if (pathname !== '/admin' && pathname !== '/admin/dashboard') {
        trail.push({ path: '/admin/dashboard', title: '管理画面' });
      }
      break;
    case 'agency':
      // 代理店ダッシュボード以外の場合は代理店ホームを追加
      if (pathname !== '/agency' && pathname !== '/agency/dashboard') {
        trail.push({ path: '/agency/dashboard', title: '代理店ポータル' });
      }
      break;
    case 'partner':
      if (pathname !== '/partner' && pathname !== '/partner/dashboard') {
        trail.push({ path: '/partner/dashboard', title: 'パートナーポータル' });
      }
      break;
    case 'shared-app':
      // 共有ユーザーダッシュボード以外の場合はホームを追加
      if (pathname !== '/shared-app' && pathname !== '/shared-app/dashboard') {
        trail.push({ path: '/shared-app/dashboard', title: 'ホーム' });
      }
      break;
  }

  // 親ルートを追加（詳細ページの場合）
  if (config.dynamic && config.path !== pathname) {
    const parentConfig = getBreadcrumbConfig(config.path);
    if (parentConfig && parentConfig.path !== trail[trail.length - 1]?.path) {
      trail.push(parentConfig);
    }
  }

  // 現在のページを追加
  trail.push({ ...config, path: pathname });

  return trail;
}
